import { apiService } from '@/services/api'
import { wineService } from '@/services/wine'
import {
    CreateReviewRequest,
    Review,
    ReviewListParams,
    ScannedWine,
    Wine,
    WineListParams,
} from '@/types'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export const useWineStore = defineStore('wine', () => {
    // State
    const wines = ref<Wine[]>([])
    const scannedWines = ref<ScannedWine[]>([])
    const currentWine = ref<Wine | null>(null)
    const lastWine = ref<Wine | null>(null)
    const reviews = ref<Review[]>([])
    const isLoading = ref(false)
    const error = ref<string | null>(null)
    const pagination = ref<{
        page: number
        limit: number
        total: number
        total_pages: number
    } | null>(null)

    // Computed
    const winesCount = computed(() => wines.value.length)
    const reviewsCount = computed(() => reviews.value.length)
    const averageRating = computed(() => {
        if (reviews.value.length === 0) return 0
        const sum = reviews.value.reduce((acc, review) => acc + review.rating, 0)
        return sum / reviews.value.length
    })

    // Wine methods
    const loadWines = async (params?: WineListParams) => {
        try {
            isLoading.value = true
            error.value = null

            // Backend returns { items, page, limit, total, pages }
            const response = await apiService.getWines(params)

            // Реальная структура: response.data уже содержит {items, page, limit, total, pages}
            const responseData = response?.data as
                | { items: Wine[]; page: number; limit: number; total: number; pages: number }
                | undefined
            const items: Wine[] = Array.isArray(responseData?.items) ? responseData!.items : []
            wines.value = items
            pagination.value =
                responseData && typeof responseData.page === 'number'
                    ? {
                          page: responseData.page,
                          limit: responseData.limit,
                          total: responseData.total,
                          total_pages: responseData.pages,
                      }
                    : null
            return { success: true, data: { items, pagination: pagination.value } }
        } catch (err: unknown) {
            error.value = (err as Error)?.message || 'Ошибка загрузки вин'
            return { success: false, error: error.value }
        } finally {
            isLoading.value = false
        }
    }

    const loadLastWine = async () => {
        try {
            isLoading.value = true
            error.value = null

            // Use backend shape: { items, page, limit, total, pages }
            const response = await apiService.getWines({
                limit: 1,
                page: 1,
                sortBy: 'created_at:desc',
            })

            // Реальная структура: response.data уже содержит {items, page, limit, total, pages}
            const responseData = response?.data as
                | { items: Wine[]; page: number; limit: number; total: number; pages: number }
                | undefined
            const items: Wine[] = Array.isArray(responseData?.items) ? responseData!.items : []
            lastWine.value = items[0] || null
            return { success: true, data: lastWine.value }
        } catch (err: unknown) {
            error.value = (err as Error)?.message || 'Ошибка загрузки последнего вина'
            return { success: false, error: error.value }
        } finally {
            isLoading.value = false
        }
    }

    const loadWine = async (id: number) => {
        try {
            isLoading.value = true
            error.value = null

            const response = await apiService.getWine(id)
            if (response.success && response.data) {
                currentWine.value = response.data
                return { success: true, data: currentWine.value }
            } else {
                error.value = response.error?.message || 'Ошибка загрузки вина'
                return { success: false, error: response.error }
            }
        } catch (err: unknown) {
            error.value = (err as Error)?.message || 'Ошибка загрузки вина'
            return { success: false, error: error.value }
        } finally {
            isLoading.value = false
        }
    }

    const addWine = (wine: Wine) => {
        const existingIndex = wines.value.findIndex((w) => w.id === wine.id)
        if (existingIndex === -1) {
            wines.value.unshift(wine)
        } else {
            wines.value[existingIndex] = wine
        }
    }

    const updateWine = (wine: Wine) => {
        const index = wines.value.findIndex((w) => w.id === wine.id)
        if (index !== -1) {
            wines.value[index] = wine
        }

        if (currentWine.value?.id === wine.id) {
            currentWine.value = wine
        }
    }

    const removeWine = (id: number) => {
        wines.value = wines.value.filter((w) => w.id !== id)
        if (currentWine.value?.id === id) {
            currentWine.value = null
        }
    }

    // Review methods
    const loadReviews = async (params?: ReviewListParams) => {
        try {
            isLoading.value = true
            error.value = null

            const result = await wineService.getReviews(params)

            if (result.success && result.data) {
                reviews.value = result.data.data
                pagination.value = result.data.pagination
                return { success: true, data: result.data }
            } else {
                error.value = result.error || 'Ошибка загрузки отзывов'
                return { success: false, error: result.error }
            }
        } catch (err: unknown) {
            const msg = (err as Error).message
            error.value = msg || 'Ошибка загрузки отзывов'
            return { success: false, error: msg }
        } finally {
            isLoading.value = false
        }
    }

    const loadWineReviews = async (wineId: number, params?: ReviewListParams) => {
        try {
            isLoading.value = true
            error.value = null

            const result = await wineService.getWineReviews(wineId, params)

            if (result.success && result.data) {
                reviews.value = result.data.data
                pagination.value = result.data.pagination
                return { success: true, data: result.data }
            } else {
                error.value = result.error || 'Ошибка загрузки отзывов'
                return { success: false, error: result.error }
            }
        } catch (err: unknown) {
            const msg = (err as Error).message
            error.value = msg || 'Ошибка загрузки отзывов'
            return { success: false, error: msg }
        } finally {
            isLoading.value = false
        }
    }

    const deleteReview = async (id: number) => {
        try {
            isLoading.value = true
            error.value = null

            const result = await wineService.deleteReview(id)

            if (result.success) {
                reviews.value = reviews.value.filter((r) => r.id !== id)

                // Update current wine if it had this review
                if (currentWine.value?.user_review?.id === id) {
                    currentWine.value.user_review = undefined
                }

                return { success: true }
            } else {
                error.value = result.error || 'Ошибка удаления отзыва'
                return { success: false, error: result.error }
            }
        } catch (err: unknown) {
            const msg = (err as Error).message
            error.value = msg || 'Ошибка удаления отзыва'
            return { success: false, error: msg }
        } finally {
            isLoading.value = false
        }
    }

    // Scan methods
    const scanWine = async (image: File) => {
        try {
            isLoading.value = true
            error.value = null

            const result = await wineService.scanWine(image)

            if (result.success && result.data) {
                // Add scanned wine to the list if it's new
                addWine(result.data.wine)
                return { success: true, data: result.data }
            } else {
                error.value = result.error || 'Ошибка сканирования'
                return { success: false, error: result.error }
            }
        } catch (err: unknown) {
            const msg = (err as Error).message
            error.value = msg || 'Ошибка сканирования'
            return { success: false, error: msg }
        } finally {
            isLoading.value = false
        }
    }
    const scansRequestId = ref(0)
    const loadScans = async (params?: { page?: number; limit?: number }) => {
        try {
            isLoading.value = true
            error.value = null
            const reqId = ++scansRequestId.value
            console.log('🔍 Loading scanned wines from store...')

            const response = await apiService.getScans(params)

            // Если пока мы ждали, стартовал новый запрос — игнорируем этот ответ
            if (reqId !== scansRequestId.value) {
                return { success: false, error: 'stale' }
            }

            if (response.success && response.data) {
                scannedWines.value = response.data.items || []
                pagination.value = {
                    page: response.data.page,
                    limit: response.data.limit,
                    total: response.data.total,
                    total_pages: response.data.pages,
                }
                console.log('✅ Loaded scanned wines to store:', scannedWines.value.length)
                return { success: true, data: response.data }
            } else {
                error.value = response.error?.message || 'Ошибка загрузки сканов'
                return { success: false, error: response.error }
            }
        } catch (err: unknown) {
            error.value = (err as Error)?.message || 'Ошибка загрузки сканов'
            return { success: false, error: { code: -1, message: error.value } }
        } finally {
            isLoading.value = false
        }
    }

    const createScan = async (bottleId: string) => {
        try {
            isLoading.value = true
            error.value = null

            const response = await apiService.createScan(bottleId)
            if (response.success && response.data) {
                // Add scanned wine to the list
                addWine(response.data)
                return { success: true, data: response.data }
            } else {
                error.value = response.error?.message || 'Ошибка создания скана'
                return { success: false, error: response.error }
            }
        } catch (err: unknown) {
            error.value = (err as Error)?.message || 'Ошибка создания скана'
            return { success: false, error: { code: -1, message: error.value } }
        } finally {
            isLoading.value = false
        }
    }

    const loadScan = async (scanId: string) => {
        try {
            isLoading.value = true
            error.value = null

            const response = await apiService.getScan(scanId)
            if (response.success && response.data) {
                return { success: true, data: response.data }
            } else {
                error.value = response.error?.message || 'Ошибка загрузки скана'
                return { success: false, error: response.error }
            }
        } catch (err: unknown) {
            error.value = (err as Error)?.message || 'Ошибка загрузки скана'
            return { success: false, error: { code: -1, message: error.value } }
        } finally {
            isLoading.value = false
        }
    }

    const recognizeWine = async (imageFile: File) => {
        try {
            isLoading.value = true
            error.value = null

            const response = await apiService.recognizeWine(imageFile)
            if (response.success && response.data) {
                return { success: true, data: response.data }
            } else {
                error.value = response.error?.message || 'Ошибка распознавания вина'
                return { success: false, error: response.error }
            }
        } catch (err: unknown) {
            error.value = (err as Error)?.message || 'Ошибка распознавания вина'
            return { success: false, error: { code: -1, message: error.value } }
        } finally {
            isLoading.value = false
        }
    }

    // Utility methods
    const getWineById = (id: number) => {
        return wines.value.find((w) => w.id === id) || null
    }

    const getReviewById = (id: number) => {
        return reviews.value.find((r) => r.id === id) || null
    }

    const clearError = () => {
        error.value = null
    }

    const clearData = () => {
        scansRequestId.value++
        wines.value = []
        scannedWines.value = []
        currentWine.value = null
        lastWine.value = null
        reviews.value = []
        pagination.value = null
        error.value = null
    }

    // Search and filter methods
    const searchWines = (query: string) => {
        if (!query.trim()) return wines.value

        const lowercaseQuery = query.toLowerCase()
        return wines.value.filter(
            (wine) =>
                wine.name.toLowerCase().includes(lowercaseQuery) ||
                wine.winemaker?.name.toLowerCase().includes(lowercaseQuery) ||
                wine.description?.toLowerCase().includes(lowercaseQuery)
        )
    }

    const filterWinesByWinemaker = (winemakerId: number) => {
        return wines.value.filter((wine) => wine.winemaker_id === winemakerId)
    }

    const filterWinesByYear = (year: number) => {
        return wines.value.filter((wine) => wine.year === year)
    }

    const sortWines = (sortBy: 'name' | 'year' | 'rating' | 'created_at') => {
        const sorted = [...wines.value]

        switch (sortBy) {
            case 'name':
                return sorted.sort((a, b) => a.name.localeCompare(b.name))
            case 'year':
                return sorted.sort((a, b) => (b.year || 0) - (a.year || 0))
            case 'rating':
                return sorted.sort((a, b) => (b.average_rating || 0) - (a.average_rating || 0))
            case 'created_at':
                return sorted.sort(
                    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
                )
            default:
                return sorted
        }
    }

    // Create review method
    const createReview = async (reviewData: CreateReviewRequest) => {
        try {
            isLoading.value = true
            error.value = null
            console.log('🏪 Store: Creating review...', reviewData)

            const response = await apiService.createReview(reviewData)
            console.log('🏪 Store: API response:', response)
            if (response.success && response.data) {
                // Add review to current wine if it matches
                if (currentWine.value && currentWine.value.id === reviewData.wine_id) {
                    currentWine.value.user_review = response.data
                    // Update average rating if needed
                    if (currentWine.value.average_rating !== undefined) {
                        // Recalculate or update from backend
                        // For now, just refresh the wine data
                        await loadWine(reviewData.wine_id)
                    }
                }

                // Add to reviews list
                const existingIndex = reviews.value.findIndex(
                    (r) => r.user_id === response.data?.user_id && r.wine_id === reviewData.wine_id
                )
                if (existingIndex >= 0) {
                    reviews.value[existingIndex] = response.data
                } else {
                    reviews.value.unshift(response.data)
                }

                // Update review in scanned wines list
                const scannedWineIndex = scannedWines.value.findIndex(
                    (sw) => sw.wine.id === reviewData.wine_id
                )
                if (scannedWineIndex >= 0) {
                    scannedWines.value[scannedWineIndex].review = response.data
                    console.log('✅ Updated review in scanned wines list')
                }
            }

            return response
        } catch (error) {
            console.error('Create review error:', error)
            return { success: false, error: { code: -1, message: 'Failed to create review' } }
        } finally {
            isLoading.value = false
        }
    }

    // Update review method
    const updateReview = async (reviewId: number, reviewData: Partial<CreateReviewRequest>) => {
        try {
            isLoading.value = true
            error.value = null

            const response = await apiService.updateReview(reviewId, reviewData)
            if (response.success && response.data) {
                // Update in current wine if it matches
                if (currentWine.value && currentWine.value.user_review?.id === reviewId) {
                    currentWine.value.user_review = response.data
                    // Refresh wine data to get updated average rating
                    await loadWine(currentWine.value.id)
                }

                // Update in reviews list
                const index = reviews.value.findIndex((r) => r.id === reviewId)
                if (index >= 0) {
                    reviews.value[index] = response.data
                }
            }

            return response
        } catch (error) {
            console.error('Update review error:', error)
            return { success: false, error: { code: -1, message: 'Failed to update review' } }
        } finally {
            isLoading.value = false
        }
    }

    return {
        // State
        wines,
        scannedWines,
        currentWine,
        reviews,
        isLoading,
        error,
        pagination,

        // Computed
        winesCount,
        reviewsCount,
        averageRating,

        // Wine methods
        loadWines,
        loadWine,
        getWine: loadWine, // Alias for consistency
        loadLastWine,
        addWine,
        updateWine,
        removeWine,

        // Review methods
        loadReviews,
        loadWineReviews,
        createReview,
        updateReview,
        deleteReview,

        // Scan methods
        scanWine,
        loadScans,
        createScan,
        loadScan,
        recognizeWine,

        // Utility methods
        getWineById,
        getReviewById,
        clearError,
        clearData,

        // Search and filter methods
        searchWines,
        filterWinesByWinemaker,
        filterWinesByYear,
        sortWines,

        lastWine,
    }
})
